/**
 * i18n-utils.js - FileToQR 다국어 지원 유틸리티
 * 버전: 1.2.0
 * 최종 업데이트: 2025-07-20
 * 
 * 이 모듈은 다국어 지원 관련 유틸리티 함수를 제공합니다.
 * - 언어 코드 감지 및 관리
 * - 텍스트 번역
 * - 다국어 경로 처리
 */

// 의존성
import UrlUtils from './url-utils.js';
import { log } from './log-utils.js';

// 전역 객체 설정
window.FileToQR = window.FileToQR || {};
window.FileToQR.i18n = window.FileToQR.i18n || {};

const I18N_UTILS_ID = 'I18nUtils';

/**
 * 중첩 객체에서 dot notation(예: 'tips.scan.desc')으로 값 탐색
 * @param {Object} obj - 탐색할 객체
 * @param {string} path - 'a.b.c' 형태의 경로
 * @returns {*} 탐색된 값 또는 undefined
 */
function getNested(obj, path) {
  return path.split('.').reduce((o, k) => (o || {})[k], obj);
}

/**
 * 국제화(i18n) 유틸리티 클래스
 * 언어 감지, 번역 데이터 로딩 및 적용, URL 업데이트 등을 담당합니다.
 */
class I18nUtils {
  constructor() {
    this.currentLanguage = 'en';
    this.translations = {};
    this.isInitialized = false;
    this.options = {
      useSavedLang: true,
      detectBrowserLang: true,
    };
  }

  async init(options = {}) {
    if (this.isInitialized) {
      log(I18N_UTILS_ID, 'Already initialized.');
      return;
    }
    this.options = { ...this.options, ...options };
    log(I18N_UTILS_ID, `Initialization started with options: ${JSON.stringify(this.options)}`);

    this.currentLanguage = this.detectLanguage();
    log(I18N_UTILS_ID, `Current language detected: ${this.currentLanguage}`);

    await this.loadTranslations(this.currentLanguage);
    this.updatePageElements();

    document.documentElement.lang = this.currentLanguage;
    
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: this.currentLanguage } }));
    }

    this.isInitialized = true;
    log(I18N_UTILS_ID, 'Initialization completed');
  }

  detectLanguage() {
    if (this.options.useSavedLang) {
      try {
        const savedLang = localStorage.getItem('userLanguage');
        if (savedLang) {
          log(I18N_UTILS_ID, `Language detected from saved settings: ${savedLang}`);
          return savedLang;
        }
      } catch (e) {
        log(I18N_UTILS_ID, 'Could not access localStorage. Skipping saved language detection.', 'warn');
      }
    }
    if (this.options.detectBrowserLang && typeof navigator !== 'undefined') {
      const browserLang = navigator.language.split('-')[0];
      log(I18N_UTILS_ID, `Browser language detected: ${browserLang}`);
      return browserLang;
    }
    return 'en'; // Default language
  }

  async loadTranslations(lang) {
    const translationModules = import.meta.glob('/assets/i18n/*.json');
    const translationPath = `/assets/i18n/${lang}.json`;

    try {
      if (translationModules[translationPath]) {
        const module = await translationModules[translationPath]();
        this.translations = module.default;
        log(I18N_UTILS_ID, `Translation file loaded successfully: ${translationPath}`);
      } else {
        throw new Error(`Translation file not found for ${lang}.`);
      }
    } catch (error) {
      log(I18N_UTILS_ID, `Error loading translation file for ${lang}, falling back to 'en': ${error.message}`, 'error');
      if (lang !== 'en') {
        await this.loadTranslations('en');
      } else {
        log(I18N_UTILS_ID, 'Failed to load fallback English translation file.', 'error');
        this.translations = {}; // Clear translations if fallback also fails
      }
    }
  }

  t(key, params = {}) {
    let translation = key.split('.').reduce((obj, k) => obj && obj[k], this.translations);
    if (typeof translation === 'string') {
      Object.keys(params).forEach(pKey => {
        translation = translation.replace(new RegExp(`{{${pKey}}}`, 'g'), params[pKey]);
      });
      return translation;
    }
    log(I18N_UTILS_ID, `Translation not found for key: ${key}`, 'warn');
    return key;
  }

  updatePageElements() {
    const elements = document.querySelectorAll('[data-i18n]');
    elements.forEach(element => {
      const key = element.dataset.i18n;
      const translation = this.t(key);
      if (translation !== key) {
        element.innerHTML = translation;
      }
    });
  }

  async setLanguage(lang) {
    if (lang === this.currentLanguage) return;

    this.currentLanguage = lang;
    document.documentElement.lang = lang;

    try {
      localStorage.setItem('userLanguage', lang);
    } catch (e) {
      log(I18N_UTILS_ID, 'Could not access localStorage to save language.', 'warn');
    }

    await this.loadTranslations(lang);
    
    // 언어 변경이 완료되었음을 앱 전체에 알립니다.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: this.currentLanguage } }));
    }
  }
}

const i18nInstance = new I18nUtils();
export default i18nInstance; 